import { useState, useMemo, useCallback, useEffect } from "react";
import { Input, Spin } from "antd";
import { useIntl } from "react-intl";

import {
  UseAutoCompleteProps,
  UseAutoCompleteReturnValue,
  AutoCompleteOption,
} from "./types";
import { mapAutoCompleteOption } from "./utils";

export const useAutoComplete = <TOption,>({
  options: propOptions,
  value: propValue,
  onChange,
  valueProp,
  labelProp,
  loading,
  placeholder,
  ...props
}: UseAutoCompleteProps<TOption>): UseAutoCompleteReturnValue<TOption> => {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState<string>();
  const [, setSelected] = useState(
    mapAutoCompleteOption(propValue, valueProp, labelProp)
  );

  const options = useMemo(
    () =>
      propOptions
        .map((option) => mapAutoCompleteOption(option, valueProp, labelProp))
        .filter((option): option is AutoCompleteOption<TOption> => !!option),
    [labelProp, propOptions, valueProp]
  );

  const handleChange = useCallback(
    (
      newSearchValue: string | null | undefined,
      newSelecteds?: AutoCompleteOption<TOption> | AutoCompleteOption<TOption>[]
    ) => {
      let newSelected = mapAutoCompleteOption(
        Array.isArray(newSelecteds) ? newSelecteds[0] : newSelecteds,
        valueProp,
        labelProp
      );

      newSelected = newSelected?.value ? newSelected : undefined;

      setSearchValue(newSelected?.label ?? newSearchValue ?? "");
      setSelected(newSelected);
      onChange?.(newSelected);
    },
    [labelProp, onChange, valueProp]
  );

  const handleClear = useCallback(() => {
    setSelected(undefined);
    setSearchValue(undefined);
    onChange?.(undefined);
  }, [onChange]);

  useEffect(() => {
    const newValue = mapAutoCompleteOption(propValue, valueProp, labelProp);

    setSelected(newValue);
    setSearchValue(newValue?.label);
  }, [labelProp, propValue, valueProp]);

  return useMemo(
    () => ({
      options,
      searchValue,
      value: searchValue,
      filterOption: false,
      onChange: handleChange,
      onClear: handleClear,
      notFoundContent: loading ? (
        <Spin size="small" />
      ) : (
        intl.formatMessage({
          defaultMessage: "Nothing found",
        })
      ),
      children: <Input.Search {...{ placeholder }} />,
      ...props,
    }),
    [
      handleChange,
      handleClear,
      intl,
      loading,
      options,
      placeholder,
      props,
      searchValue,
    ]
  );
};
